/**
 * Health Records Service
 * API service for health records management
 */

import { axiosInstance } from '../../../api/axios.config';
import { AGENCY_ENDPOINTS, SERVICE_REQUEST_ENDPOINTS } from '../../../constants/api.constants';
import type {
  HealthRecord,
  SurgicalProcedure,
  VitalSign,
  SocialHistory,
  Hospitalization,
  MedicalVisit,
  TestDiagnostic,
  CareTeamProvider,
  MedicalCondition,
  Allergy,
  Medication,
  Immunization,
} from '../types/healthRecords.types';

export type CareRequestShareStatus =
  | 'not_shared'
  | 'shared_direct'
  | 'shared_pin_locked'
  | 'shared_pin_unlocked'
  | 'expired'
  | 'revoked';

export interface CareRequestShareMeta {
  shareStatus: CareRequestShareStatus;
  healthRecordShared?: boolean;
  shareMode?: 'direct' | 'access_pin' | null;
  expiresAt?: string | null;
  accessUnlockExpiresAt?: string | null;
  grantedSections?: string[];
  ungrantedSections?: string[];
  sectionAccess?: Record<string, boolean>;
  sectionKeys?: string[];
  sectionLabels?: Record<string, string>;
  canRequestSections?: boolean;
  pendingSectionRequests?: Array<{
    id?: string;
    sections: string[];
    sectionLabels?: string[];
    note?: string | null;
    requestedAt?: string | null;
    requestedBy?: { userId?: string; name?: string } | null;
    status: string;
  }>;
}

export interface CareRequestMessage {
  id: string;
  body: string;
  kind: 'text' | 'system' | 'section_request';
  senderType: 'consumer' | 'provider' | 'system';
  senderName?: string | null;
  relatedSectionRequestId?: string | null;
  createdAt?: string | null;
}

export interface CareRequestMessageNotificationItem {
  messageId: string;
  serviceRequestId: string;
  requestLabel: string;
  patientName?: string | null;
  agencyName?: string | null;
  preview: string;
  unreadCount: number;
  kind?: string | null;
  lastMessageAt?: string | null;
}

export interface CareRequestMessageNotificationsData {
  totalUnread: number;
  items: CareRequestMessageNotificationItem[];
}

const healthRecordsService = {
  getHealthRecords: async (_params?: any): Promise<{ data: HealthRecord[] }> => {
    return { data: [] };
  },

  /** Preferred: shares scoped to a care request (enforces shareStatus + access logging) */
  getHealthRecordSharesByServiceRequest: async (
    serviceRequestId: string,
    options?: { basicInfo?: boolean }
  ): Promise<any> => {
    const endpoint = SERVICE_REQUEST_ENDPOINTS.HEALTH_RECORD_SHARES.replace(
      '{serviceRequestId}',
      serviceRequestId
    );
    const response = await axiosInstance.get(endpoint, {
      params: options?.basicInfo ? { basicInfo: true } : undefined,
    });
    return response.data;
  },

  getCareRequestShareStatus: async (serviceRequestId: string): Promise<CareRequestShareMeta | null> => {
    try {
      const endpoint = SERVICE_REQUEST_ENDPOINTS.SHARE_STATUS.replace(
        '{serviceRequestId}',
        serviceRequestId
      );
      const response = await axiosInstance.get(endpoint);
      return (response.data?.data?.share as CareRequestShareMeta) || null;
    } catch {
      return null;
    }
  },

  unlockCareRequestShare: async (
    serviceRequestId: string,
    accessPin: string
  ): Promise<CareRequestShareMeta | null> => {
    const endpoint = SERVICE_REQUEST_ENDPOINTS.SHARE_UNLOCK.replace(
      '{serviceRequestId}',
      serviceRequestId
    );
    const response = await axiosInstance.post(endpoint, { accessPin });
    return (response.data?.data?.share as CareRequestShareMeta) || null;
  },

  requestCareRequestSections: async (
    serviceRequestId: string,
    payload: { sections: string[]; note?: string }
  ): Promise<CareRequestShareMeta | null> => {
    try {
      const endpoint = SERVICE_REQUEST_ENDPOINTS.SHARE_SECTION_REQUESTS.replace(
        '{serviceRequestId}',
        serviceRequestId
      );
      const response = await axiosInstance.post(endpoint, payload);
      return (response.data?.data?.share as CareRequestShareMeta) || null;
    } catch (error) {
      console.error('requestCareRequestSections failed', error);
      throw error;
    }
  },

  listCareRequestMessages: async (serviceRequestId: string): Promise<CareRequestMessage[]> => {
    try {
      const endpoint = SERVICE_REQUEST_ENDPOINTS.MESSAGES.replace(
        '{serviceRequestId}',
        serviceRequestId
      );
      const response = await axiosInstance.get(endpoint);
      return (response.data?.data?.messages as CareRequestMessage[]) || [];
    } catch (error) {
      console.error('listCareRequestMessages failed', error);
      throw error;
    }
  },

  sendCareRequestMessage: async (
    serviceRequestId: string,
    body: string
  ): Promise<CareRequestMessage | null> => {
    try {
      const endpoint = SERVICE_REQUEST_ENDPOINTS.MESSAGES.replace(
        '{serviceRequestId}',
        serviceRequestId
      );
      const response = await axiosInstance.post(endpoint, { body });
      return (response.data?.data?.message as CareRequestMessage) || null;
    } catch (error) {
      console.error('sendCareRequestMessage failed', error);
      throw error;
    }
  },

  listCareRequestMessageNotifications: async (): Promise<CareRequestMessageNotificationsData> => {
    try {
      const response = await axiosInstance.get(SERVICE_REQUEST_ENDPOINTS.MESSAGE_NOTIFICATIONS);
      return {
        totalUnread: response.data?.data?.totalUnread || 0,
        items: (response.data?.data?.items as CareRequestMessageNotificationItem[]) || [],
      };
    } catch (error) {
      console.error('listCareRequestMessageNotifications failed', error);
      throw error;
    }
  },

  markCareRequestMessagesRead: async (
    serviceRequestId: string,
    options?: { messageId?: string }
  ): Promise<void> => {
    try {
      const endpoint = SERVICE_REQUEST_ENDPOINTS.MESSAGES_MARK_READ.replace(
        '{serviceRequestId}',
        serviceRequestId
      );
      await axiosInstance.post(endpoint, {
        ...(options?.messageId ? { messageId: options.messageId } : {}),
      });
    } catch (error) {
      console.error('markCareRequestMessagesRead failed', error);
      throw error;
    }
  },

  /** Legacy agency listing helper (kept for non-request contexts) */
  getHealthRecordShares: async (agencyId: string, serviceRequestId: string): Promise<any> => {
    if (serviceRequestId) {
      return healthRecordsService.getHealthRecordSharesByServiceRequest(serviceRequestId);
    }
    const endpoint = AGENCY_ENDPOINTS.HEALTH_RECORD_SHARES.replace('{id}', agencyId);
    const response = await axiosInstance.get(endpoint, {
      params: { serviceRequestId },
    });
    return response.data;
  },

  getConditions: async (_recordId: string): Promise<MedicalCondition[]> => {
    return [];
  },
  getAllergies: async (_recordId: string): Promise<Allergy[]> => {
    return [];
  },
  getMedications: async (_recordId: string): Promise<Medication[]> => {
    return [];
  },
  getImmunizations: async (_recordId: string): Promise<Immunization[]> => {
    return [];
  },
  getSurgicalHistory: async (_recordId: string): Promise<SurgicalProcedure[]> => {
    return [];
  },
  getVitals: async (_recordId: string): Promise<VitalSign[]> => {
    return [];
  },
  getSocialHistory: async (_recordId: string): Promise<SocialHistory> => {
    return {};
  },
  getHospitalizations: async (): Promise<Hospitalization[]> => {
    return [];
  },
  getMedicalVisits: async (): Promise<MedicalVisit[]> => {
    return [];
  },
  getTestDiagnostics: async (): Promise<TestDiagnostic[]> => {
    return [];
  },
  getCareTeamProviders: async (): Promise<CareTeamProvider[]> => {
    return [];
  },
  getAllergiesAndConditions: async (): Promise<{
    conditions: MedicalCondition[];
    medications: Medication[];
    allergies: Allergy[];
  }> => {
    return { conditions: [], medications: [], allergies: [] };
  },
};

export default healthRecordsService;
